/**
 * The composer's view of the window's voice call (RFC 0307).
 *
 * The call itself is the window's (`sessionCalls`), so it outlives this
 * thread: switching spaces or closing the pane leaves it up, and coming back
 * to the session finds it still live. Here a composer on a session draws that
 * call when it is this session's, and offers to start one when it is not.
 */

import type { Repo } from '@automerge/automerge-repo/slim'
import { useEffect, useSyncExternalStore } from 'react'
import {
  hasVoiceOnOpen,
  takeVoiceOnOpen,
} from '../../services/voice/startOnOpen'
import type { ComposerVoice } from '../../services/voice/types'
import { sessionCalls } from './sessionCall'

export { sessionVoiceState, spokenOption } from '@mod/core/voice'

export function useSessionVoice(opts: {
  repo: Repo
  sessionId: string
  projectId?: string
  sessionTitle: string
}): ComposerVoice {
  const { repo, sessionId, projectId, sessionTitle } = opts
  const active = useSyncExternalStore(
    sessionCalls.subscribe,
    sessionCalls.current,
  )
  const target = {
    repo,
    sessionId,
    ...(projectId ? { projectId } : {}),
    title: sessionTitle,
  }

  // A call asked for from the new pane, before this session existed. The mark
  // is spent once the call has gone live or failed, so a double mount
  // (StrictMode) still finds it.
  useEffect(() => {
    if (!hasVoiceOnOpen(sessionId)) return
    const current = sessionCalls.current()
    if (current?.sessionId === sessionId) {
      if (current.voice.status !== 'connecting') takeVoiceOnOpen(sessionId)
      return
    }
    sessionCalls.start({
      repo,
      sessionId,
      ...(projectId ? { projectId } : {}),
      title: sessionTitle,
    })
  }, [repo, sessionId, projectId, sessionTitle])
  useEffect(() => {
    if (
      active?.sessionId === sessionId &&
      (active.voice.status === 'live' || active.voice.status === 'error')
    )
      takeVoiceOnOpen(sessionId)
  }, [active, sessionId])

  return active?.sessionId === sessionId
    ? active.voice
    : sessionCalls.voiceFor(target)
}
